import { Card, CardContent } from "@/components/ui/card";
import { Users, FileText, Scale, Target, Euro, AlertTriangle, Info, HelpCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const ContentSection = () => {
  const values = [
    {
      icon: Users,
      title: "Solidarité",
      description: "Regrouper les conseillers pour agir collectivement",
    },
    {
      icon: FileText,
      title: "Transparence",
      description: "Centraliser les témoignages et les informations",
    },
    {
      icon: Scale,
      title: "Défense de nos droits",
      description: "Faire valoir nos droits dans le respect du droit",
    },
  ];

  return (
    <section className="py-20 px-4 bg-gradient-to-b from-background via-secondary/10 to-background">
      <div className="max-w-6xl mx-auto space-y-16">
        {/* Main Text Content */}
        <Card className="border-0 shadow-2xl overflow-hidden bg-gradient-to-br from-card via-card to-secondary/20">
          <CardContent className="p-8 md:p-12 space-y-10 text-foreground">
            {/* Introduction */}
            <div className="space-y-4 animate-fade-in">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10">
                  <Info className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                  Qui sommes-nous ?
                </h2>
              </div>
              <p className="text-lg leading-relaxed text-foreground/90 pl-14">
                Le Collectif Ibbü réunit des conseillers ayant collaboré avec la plateforme iAdvize/Ibbü et qui
                souhaiteraient faire valoir nos droits. Il s'agit d'une initiative citoyenne menée par des pairs ; nous
                ne sommes ni avocats, ni professionnels du droit.
              </p>
            </div>

            <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />

            {/* Le Constat */}
            <div className="space-y-5 animate-fade-in" style={{ animationDelay: "0.1s" }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-accent/20 to-accent/10">
                  <AlertTriangle className="w-6 h-6 text-accent" />
                </div>
                <h3 className="text-xl md:text-2xl font-semibold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                  Le constat
                </h3>
              </div>
              <div className="space-y-4 text-base md:text-lg pl-14">
                <p className="leading-relaxed text-foreground/90">
                  Depuis plusieurs années, de nombreux conseillers auraient constaté :
                </p>
                <div className="grid gap-3">
                  {[
                    "Une baisse importante du volume de missions",
                    "Des ruptures soudaines de collaboration",
                    "Des décisions techniques ayant un impact direct sur leurs revenus et leur organisation",
                  ].map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 border border-border/30 hover:border-primary/50 transition-all duration-300 hover:shadow-md"
                    >
                      <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                      <p className="leading-relaxed text-foreground/90">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />

            {/* Les Problématiques */}
            <div className="space-y-6 animate-fade-in" style={{ animationDelay: "0.15s" }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-destructive/20 to-destructive/10">
                  <Target className="w-6 h-6 text-destructive" />
                </div>
                <h3 className="text-xl md:text-2xl font-semibold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                  Les problématiques identifiées
                </h3>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <Card className="border-border/50 overflow-hidden group hover:border-primary/50 transition-all duration-300 hover:shadow-xl">
                  <CardContent className="p-5 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
                        <Target className="w-5 h-5 text-primary" />
                      </div>
                      <h4 className="font-semibold text-foreground">Accès aux missions</h4>
                    </div>
                    <p className="text-sm leading-relaxed text-foreground/80">
                      Tests obligatoires et objectifs de performance imposés
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-border/50 overflow-hidden group hover:border-primary/50 transition-all duration-300 hover:shadow-xl">
                  <CardContent className="p-5 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
                        <Euro className="w-5 h-5 text-primary" />
                      </div>
                      <h4 className="font-semibold text-foreground">Rémunération</h4>
                    </div>
                    <p className="text-sm leading-relaxed text-foreground/80">
                      Dépendance totale au flux de conversations attribuées, modifiable à tout moment. Tarifs imposés et
                      non négociables.
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-border/50 overflow-hidden group hover:border-primary/50 transition-all duration-300 hover:shadow-xl">
                  <CardContent className="p-5 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
                        <AlertTriangle className="w-5 h-5 text-primary" />
                      </div>
                      <h4 className="font-semibold text-foreground">Transparence</h4>
                    </div>
                    <p className="text-sm leading-relaxed text-foreground/80">
                      Dépendance technique totale, absence de transparence sur les décisions, résiliations parfois sans
                      explication
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>

            <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />

            {/* Notre Action */}
            <div className="space-y-5 animate-fade-in" style={{ animationDelay: "0.15s" }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10">
                  <Scale className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl md:text-2xl font-semibold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                  Notre Objectif Commun
                </h3>
              </div>
              <div className="space-y-4 pl-14">
                <p className="text-base md:text-lg leading-relaxed text-foreground/90">
                  Face à ces pratiques perçues comme déséquilibrées, un collectif s'est formé pour permettre aux
                  <a href="/iadvize" className="text-primary hover:underline"> conseillers iAdvize</a> de se regrouper, partager leurs informations et préparer une éventuelle action groupée en
                  justice.
                </p>
                <p className="text-base md:text-lg leading-relaxed text-foreground/90">
                  Ce site a pour objectif d'informer, de centraliser les témoignages et d'accompagner chaque conseiller
                  dans la compréhension de nos droits.
                </p>
              </div>
            </div>

            {/* Call to action */}
            <div
              className="relative overflow-hidden rounded-xl bg-gradient-to-r from-primary via-primary/90 to-accent p-[2px] animate-fade-in"
              style={{ animationDelay: "0.15s" }}
            >
              <div className="bg-card rounded-lg p-6 backdrop-blur-sm">
                <div className="flex items-start gap-4">
                  <div className="p-2 rounded-lg bg-primary/10 flex-shrink-0">
                    <Scale className="w-6 h-6 text-primary" />
                  </div>
                  <p className="text-lg md:text-xl font-semibold text-foreground leading-relaxed">
                    Notre démarche : agir ensemble, avec rigueur et dans le respect du droit.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* FAQ Section */}
        <Card className="border-0 shadow-2xl overflow-hidden bg-gradient-to-br from-card via-card to-secondary/20">
          <CardContent className="p-8 md:p-12 space-y-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10">
                <HelpCircle className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                Questions Fréquentes
              </h2>
            </div>

            <Accordion type="single" collapsible className="w-full space-y-4">
              <AccordionItem value="item-1" className="border rounded-lg px-6 bg-muted/20">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="text-lg font-semibold">Qui peut rejoindre le Collectif Ibbü ?</span>
                </AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed text-muted-foreground pb-4">
                  Le Collectif Ibbü est ouvert à tous les conseillers ayant travaillé ou travaillant encore avec la
                  plateforme iAdvize/Ibbu. Que vous ayez été conseiller récemment ou il y a plusieurs années, si vous
                  souhaiteriez faire valoir vos droits, vous pouvez nous rejoindre. L'inscription est gratuite et
                  confidentielle.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="border rounded-lg px-6 bg-muted/20">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="text-lg font-semibold">Qu'est-ce que la plateforme iAdvize/Ibbü ?</span>
                </AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed text-muted-foreground pb-4">
                  <a href="/iadvize" className="text-primary hover:underline">iAdvize</a> est une plateforme de messaging conversationnel qui mettrait en relation des conseillers
                  (anciennement via Ibbu) avec des marques pour répondre aux questions de leurs clients. Les
                  conseillers, considérés comme indépendants, auraient pu rencontrer diverses problématiques liées à
                  leur statut et leurs conditions de collaboration avec la plateforme.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="border rounded-lg px-6 bg-muted/20">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="text-lg font-semibold">
                    Quels seraient les problèmes rencontrés par les conseillers iAdvize/Ibbu ?
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed text-muted-foreground pb-4">
                  Selon des témoignages de conseillers, plusieurs problématiques pourraient avoir été rencontrées : une
                  baisse importante du volume de missions sans explication claire, des ruptures soudaines de
                  collaboration, des décisions techniques impactant directement les revenus, et des questions sur le
                  statut juridique réel de leur relation avec la plateforme. Ces situations soulèveraient des
                  interrogations sur une éventuelle qualification en salariat.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="border rounded-lg px-6 bg-muted/20">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="text-lg font-semibold">Comment les conseillers Ibbü étaient-ils rémunérés ?</span>
                </AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed text-muted-foreground pb-4">
                  Les conseillers de la plateforme iAdvize/Ibbu auraient été rémunérés selon différents modèles qui
                  pourraient avoir évolué au fil du temps : rémunération à la conversation, au temps passé connecté, ou
                  à la mission. Certains conseillers auraient constaté des modifications unilatérales de ces conditions
                  de rémunération, ce qui pourrait soulever des questions juridiques sur la nature de la relation
                  contractuelle.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5" className="border rounded-lg px-6 bg-muted/20">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="text-lg font-semibold">
                    Quels sont les objectifs du Collectif Ibbü face à iAdvize ?
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed text-muted-foreground pb-4">
                  Le Collectif Ibbü vise à rassembler les témoignages des conseillers pour constituer un dossier
                  factuel, à s'informer mutuellement sur les droits potentiels, et à organiser une démarche juridique
                  collective si nécessaire. L'objectif serait de clarifier la nature de la relation entre les
                  conseillers et iAdvize/Ibbu, et de faire reconnaître les droits qui pourraient découler d'un éventuel
                  statut salarié. Notre démarche se veut respectueuse du droit et basée sur des faits.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6" className="border rounded-lg px-6 bg-muted/20">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="text-lg font-semibold">
                    Est-ce que rejoindre le Collectif Ibbü comporte des risques vis-à-vis d'iAdvize ?
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed text-muted-foreground pb-4">
                  Rejoindre un collectif pour faire valoir ses droits est un droit fondamental garanti par la loi. Vos
                  données personnelles sont traitées de manière confidentielle et ne seront communiquées à des tiers (y
                  compris à un avocat) qu'avec votre consentement explicite. Le collectif agit dans le respect du droit
                  et ne pratique aucune forme de diffamation envers iAdvize ou Ibbu.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-7" className="border rounded-lg px-6 bg-muted/20">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="text-lg font-semibold">
                    Quelle est la différence entre Ibbu (sans tréma) et Ibbü (avec tréma) ?
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed text-muted-foreground pb-4">
                  Ibbu et Ibbü désignent exactement la même plateforme. L’orthographe avec tréma (“Ibbü”) est simplement
                  une variante graphique, perçue comme plus récente ou plus distinctive. En pratique, beaucoup écrivent
                  “Ibbu” sans tréma, car c’est plus simple à saisir au clavier. Ces deux formes renvoient au même
                  service de mise en relation entre conseillers et marques, via la plateforme iAdvize. Notre collectif
                  utilise l’orthographe “Ibbü”, mais représente l’ensemble des conseillers, quelle que soit la période
                  ou l’orthographe utilisée.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        {/* Values Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {values.map((value, index) => {
            const Icon = value.icon;
            return (
              <Card
                key={index}
                className="border-0 shadow-md hover:shadow-lg transition-all duration-300 animate-fade-in"
                style={{ animationDelay: `${Math.min(index * 0.1, 0.15)}s` }}
              >
                <CardContent className="p-8 text-center space-y-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10">
                    <Icon className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground">{value.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ContentSection;
